import { select } from 'd3-selection'
import { queue } from 'd3-queue'
import { request } from 'd3-request'
import { format } from 'd3-format'

import { URLS, IMPT_INFO_default, IMPT_INFO_EUROPE_default, IMPT_INFO_GP_default, TEST_INFO_default, TEST_INFO_EUROPE_default, GLOSSARY_default, PDF_IMG_default,
  CSS_PATH_default, WT_LOGO } from '../constants.js'

const IMPT_INFO = window.portfolioMapper.importantInformationURL || IMPT_INFO_default;
const TEST_INFO = window.portfolioMapper.stressTestMethodologyURL || TEST_INFO_default;

const IE11_ADDITIONAL_DIV = "\\<div\\ style=\"margin:\\ 0px;\\ padding:\\ 0px;\\ border:\\ 0px\\ currentColor;\\ border\\-image:\\ none;\\ width:\\ \\d{1,}\\.{0,1}\\d{0,}px;\\ height:\\ \\d{1,}\\.{0,1}\\d{0,}px;\\ font\\-size:\\ 1em;\\ float:\\ none;\\ position:\\ static;\\ border\\-spacing:\\ 0;\"\\ /\\>";
const IE11_DASHBOARD_H3 = "\\<h3\\ style=\"left:\\ \\d{1,}\\.{0,1}\\d{0,}px;\\ width:\\ auto;\\ right:\\ \\d{1,}\\.{0,1}\\d{0,}px;\\ bottom:\\ auto;\\ margin\\-top:\\ 0px;\\ margin\\-right:\\ 0px;\\ margin\\-left:\\ 0px;\\ position:\\ fixed;\"\\>";


const parseTextFromPage = (r, tag) => {
  const raw = r.documentElement.getElementsByClassName(tag || 'mobile-container');
  let clean = "";
  if(raw.length){
    clean = Array.from(raw[0].children).reduce((t,v) => t + v.outerHTML, '');
  }
  return clean;
}

const getStaticText = (url, tag, callback) => {
  let xhr = new XMLHttpRequest();
  xhr.onload = function(){
    callback(null, parseTextFromPage(this.responseXML, tag))
  }
  xhr.open('GET',url);
  xhr.responseType='document';
  xhr.send();
}

const getStaticCSS = (url, callback) => {
  request(url)
    .response(xhr => xhr.responseText)
    .get(callback)
}

export const retrievePDF = (dispatch, getState) => {
  const state = getState();
  const { 
    headerTitle, logoURL, webAddress, mainCssURL, footerComplianceText, appName, firstName, lastName,
    importantInformationURL, importantInformationGPURL, stressTestMethodologyURL, glossaryURL, coverPageImageURL, disclosureHTMLTag
  } = window.portfolioMapper
  const whiteLabelColors = ['one','two','three','four','five','six','seven','eight','nine'].reduce((t,v) => 
    ({ ...t, [v]: getComputedStyle(document.getElementsByClassName('App')[0]).getPropertyValue('--white-label-color-'+v) }),{})
  const isGpPdf = state.clientPdf.status === "pending"

  //  Get the right root div
  let appId = 'root';
  if(window.dpd && window.dpd.appId) {
    appId = window.dpd.appId;
  }

  const docHtml = document.getElementById(appId).cloneNode(true);
  dispatch({ type: "SET_PDF_STATUS", status: "pending" });

  const nodeToRemove = select(docHtml).select("#fade-in").select("stop:nth-child(3)").node();
  nodeToRemove.parentNode.removeChild(nodeToRemove);
  for(const k in state.clientPdf.sections){
    select(docHtml).select("section." + k.replace(" ","_").toLowerCase() ).classed("hide-client-pdf", isGpPdf && !state.clientPdf.sections[k])
  }
  for(const j in state.clientPdf.stressTests){
    select(docHtml).select(".stress-test." + j.replace(" ","_").toLowerCase() ).classed("hide-client-pdf", isGpPdf && !state.clientPdf.stressTests[j])
  }
  if(isGpPdf){
    select(docHtml).select('.growth-explanation-gp-replace').text(state.complianceText.growthExplanationGP)
    select(docHtml).select('.growth-disclosure-gp-replace').html(state.complianceText.growthDisclosureGP)
  }
  if(isGpPdf && state.wtRegion === 'US'){ // BPS to % for US GP PDF (also #2 below)
    select(docHtml).select(".key-metric-F").selectAll('td.figure').text(function(){
      return format('.2f')(+select(this).text()/100)
    })
  }

  let docHtmlString = new XMLSerializer().serializeToString(docHtml);

  // If the app has the default relative URL (different for US and EU) then add the right domain 
  // (and iso-code) so the pdf has the right links 
  if (IMPT_INFO === IMPT_INFO_default || IMPT_INFO === IMPT_INFO_EUROPE_default){
    var re_impt_info = new RegExp(IMPT_INFO,"g");
    docHtmlString = docHtmlString.replace(re_impt_info, URLS[state.wtRegion]+IMPT_INFO);
  }
  if (TEST_INFO === TEST_INFO_default || TEST_INFO === TEST_INFO_EUROPE_default){
    var re_test_info = new RegExp(TEST_INFO,"g");
    docHtmlString = docHtmlString.replace(re_test_info, URLS[state.wtRegion]+TEST_INFO);
  }
    
  if(isGpPdf && state.wtRegion === 'US'){ // BPS to % for US GP PDF (also #1 above)
    docHtmlString = docHtmlString
      .replace(/[0-9]+bps/g, d => format('.2%')(+d.slice(0, -3)/10000))
      .replace(/bps/g, '%')
  }
  const reportNameLabel = state.reportName ? state.reportName : state.complianceText.unnamedPortfolioLabel;
  const portfolioName = (state.clientPdf.portfolioName || reportNameLabel || state.complianceText.unnamedPortfolioLabel);

  // Strip out Internet Explorer 11 additional DIV
  var ie11_extra = new RegExp(IE11_ADDITIONAL_DIV,"g");
  docHtmlString = docHtmlString.replace(ie11_extra, '');
  var ie11_dashboard = new RegExp(IE11_DASHBOARD_H3,"g");
  docHtmlString = docHtmlString.replace(ie11_dashboard, '<h3>');

  dispatch({ type: 'SET_CUSTOM_STRESS_TEST_OPEN', open: true }) // don't include in the first PDF run

  queue()
  .defer(getStaticText, (isGpPdf
    ? importantInformationGPURL || IMPT_INFO_GP_default
    : importantInformationURL || IMPT_INFO_default), disclosureHTMLTag)
  .defer(getStaticText, glossaryURL || GLOSSARY_default, disclosureHTMLTag)
  .defer(getStaticText, stressTestMethodologyURL || TEST_INFO_default, disclosureHTMLTag)
  .defer(getStaticCSS, (mainCssURL || CSS_PATH_default))
  .await(function(error, imptInfo, glossary, stressTestMethodology, stylesheet){
    if(error) throw error;

    for(var k in whiteLabelColors){
      var re = new RegExp(`var\\(\\-\\-white\\-label\\-color\\-${k}\\)`,'g')
      stylesheet = stylesheet.replace(re, whiteLabelColors[k])
    }

    let leftLogo = logoURL || state.clientPdf.defaultLogo
    if (isGpPdf && state.clientPdf.logoKey !== 'defaultLogo') {
      leftLogo = state.clientPdf[state.clientPdf.logoKey]
    }

    fetch(`${process.env.REACT_APP_API_URL}/pdf/create_and_save/`, {
      method: "POST",
      body: "value=" +
        encodeURIComponent(
          "<!DOCTYPE html><meta http-equiv='Content-Type' content='text/html; charset=UTF-8' /><html>" +
            // '<head><link rel="stylesheet" type="text/css" href="http://prototypes.two-n.com/wt-portfolio-analysis-dev/static/css/main.css"></head>'+
            '<head><style>'+stylesheet+'</style></head>'+
            '<div class="pdf-cover"><div><img id="big-img" src='+(coverPageImageURL || PDF_IMG_default)+' /><div class="pt-name">'+
            portfolioName +
            '</div></div>'+
            '<div class="footer"><div class="left"><img id="wt-logo" src="'+
              leftLogo
              +'" /><br/><br/><span class="'+(isGpPdf ? 'gp' : '')+'">'+
              (state.clientPdf.clientName
                ? state.complianceText.gpPDFPreparedFor + ": " +state.clientPdf.clientName
                : state.complianceText.gpPDFReportRunBy + ": " +(firstName || "")+" "+(lastName || "") )+
              '</span></div>'+
            '<div class="right"><span class="powered-by">' + state.complianceText.poweredByLabel + '</span><img id="dpd-logo" src="'+WT_LOGO+'" /><br/><br/><span>'+
              (isGpPdf 
                ? state.complianceText.gpPDFDistributorLanguage
                : (footerComplianceText || state.complianceText.gpPDFForFinancialProfessionalUseOnly))+
            '</span></div></div></div>'+
            '<div class="pdf-impt-info">'+
              (isGpPdf 
              ? '<div><h4>' + state.complianceText.gpPDFFirstPageFooter + '</h4></div>'
              : '') +
              imptInfo+
              '</div>'+
            '<div class="pdf-shell" >' +
              docHtmlString +
              '</div>'+
            (isGpPdf ? '<div class="pdf-impt-info">'+glossary+'</div>' : '')+
            '</html>'
        ) +
        "&name="+encodeURIComponent(portfolioName) +
        "&clientEmail="+encodeURIComponent(state.clientEmail) +
        "&preparedForEmail="+encodeURIComponent(state.entry.original.preparedForEmail) +
        "&white_label_color_one="+encodeURIComponent(whiteLabelColors.one) +
        "&white_label_color_two="+encodeURIComponent(whiteLabelColors.two) +
        "&white_label_color_two="+encodeURIComponent(whiteLabelColors.two) +
        (state.analysis[0] ? "&portfolioId="+encodeURIComponent(state.analysis[0].id) : '') +
        (logoURL ? "&logoURL="+logoURL : '')+
        (headerTitle ? "&headerTitle="+headerTitle : '')+
        (webAddress ? "&webAddress="+webAddress : '')+
        (footerComplianceText ? "&footerComplianceText="+footerComplianceText : "&footerComplianceText="+state.complianceText.gpPDFForFinancialProfessionalUseOnly)+
        (appName ? "&appName="+appName : '')+
        (isGpPdf ? "&pdfType=GP" : ""),
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: state.token && `Token ${state.token}`
      }
    })
      .then(d => {
        if (d.status >= 400) {
          dispatch({ type: "SET_PDF_STATUS", status: "unavailable" });
          dispatch({ type: "LOADING_COMPLETE" })
          return;
        } else {
          return d.blob().then(blob => {
            const url = window.URL.createObjectURL(blob);
            if(isGpPdf){
              const a = document.getElementById("pdf-url-gp") || document.createElement("a");
              document.body.appendChild(a);
              a.setAttribute("id", "pdf-url-gp");
              a.setAttribute("style","display: none");
              if(window.navigator.msSaveOrOpenBlob){
                a.addEventListener('click',() => window.navigator.msSaveOrOpenBlob(blob, "Portfolio Analysis Report.pdf"));
              } else {
                a.href = url;
                a.download = "Portfolio Analysis Report.pdf";
                a.target = '_blank';
              }
              a.click();
              dispatch({ type: "SET_CLIENT_PDF", key: "status", value: "export" })
              dispatch({ type: "LOADING_COMPLETE" })
            } else {            
              const a = document.getElementById("pdf-url-fp") || document.createElement("a");
              document.body.appendChild(a);
              a.setAttribute("id", "pdf-url-fp");
              a.setAttribute("style","display: none");
              if(window.navigator.msSaveOrOpenBlob){
                a.addEventListener('click',() => window.navigator.msSaveOrOpenBlob(blob, "Portfolio Analysis Report.pdf"));
              } else {
                a.href = url;
                a.download = "Portfolio Analysis Report.pdf";
                a.target = '_blank';
              }
              dispatch({ type: "SET_CLIENT_PDF", key: "status", value: "unsent" }); // if FP has rerun, GP needs to be rerun too
            }
            dispatch({ type: "SET_PDF_STATUS", status: "export" });
          });
        }
      })
      .catch(error => {
        console.error(error);
        dispatch({ type: "SET_PDF_STATUS", status: "unavailable" });
      });
  })

};

export const downloadPDF = (dispatch, getState) => {
  const state = getState();
  if (state.pdfStatus === "export") {
    const link = document.getElementById("pdf-url-fp");

    // Force a mouse click and then remove the link
    let evt = new MouseEvent('click', {
      'view': window,
      'bubbles': true,
      'cancelable': false
    });
    link.dispatchEvent(evt);
    (window.URL || window.webkitURL).revokeObjectURL(link.href);
  }
};

export const downloadClientPDF = (dispatch, getState) => {
  const state = getState();
  if (state.clientPdf.status === "export") {
    const link = document.getElementById("pdf-url-gp");
    // Force a mouse click and then remove the link
    let evt = new MouseEvent('click', {
      'view': window,
      'bubbles': true,
      'cancelable': false
    });
    link.dispatchEvent(evt);
    (window.URL || window.webkitURL).revokeObjectURL(link.href);
  }
};

// Array.from() polyfill. See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/from#Polyfill
// Production steps of ECMA-262, Edition 6, 22.1.2.1
if (!Array.from) {
  Array.from = (function () {
    var toStr = Object.prototype.toString;
    var isCallable = function (fn) {
      return typeof fn === 'function' || toStr.call(fn) === '[object Function]';
    };
    var toInteger = function (value) {
      var number = Number(value);
      if (isNaN(number)) { return 0; }
      if (number === 0 || !isFinite(number)) { return number; }
      return (number > 0 ? 1 : -1) * Math.floor(Math.abs(number));
    };
    var maxSafeInteger = Math.pow(2, 53) - 1;
    var toLength = function (value) {
      var len = toInteger(value);
      return Math.min(Math.max(len, 0), maxSafeInteger);
    };

    // The length property of the from method is 1.
    return function from(arrayLike/*, mapFn, thisArg */) {
      // 1. Let C be the this value.
      var C = this;

      // 2. Let items be ToObject(arrayLike).
      var items = Object(arrayLike);

      // 3. ReturnIfAbrupt(items).
      if (arrayLike == null) {
        throw new TypeError('Array.from requires an array-like object - not null or undefined');
      }

      // 4. If mapfn is undefined, then let mapping be false.
      var mapFn = arguments.length > 1 ? arguments[1] : void undefined;
      var T;
      if (typeof mapFn !== 'undefined') {
        // 5. else
        // 5. a If IsCallable(mapfn) is false, throw a TypeError exception.
        if (!isCallable(mapFn)) {
          throw new TypeError('Array.from: when provided, the second argument must be a function');
        }

        // 5. b. If thisArg was supplied, let T be thisArg; else let T be undefined.
        if (arguments.length > 2) {
          T = arguments[2];
        }
      }

      // 10. Let lenValue be Get(items, "length").
      // 11. Let len be ToLength(lenValue).
      var len = toLength(items.length);

      // 13. If IsConstructor(C) is true, then
      // 13. a. Let A be the result of calling the [[Construct]] internal method
      // of C with an argument list containing the single item len.
      // 14. a. Else, Let A be ArrayCreate(len).
      var A = isCallable(C) ? Object(new C(len)) : new Array(len);

      // 16. Let k be 0.
      var k = 0;
      // 17. Repeat, while k < len… (also steps a - h)
      var kValue;
      while (k < len) {
        kValue = items[k];
        if (mapFn) {
          A[k] = typeof T === 'undefined' ? mapFn(kValue, k) : mapFn.call(T, kValue, k);
        } else {
          A[k] = kValue;
        }
        k += 1;
      }
      // 18. Let putStatus be Put(A, "length", len, true).
      A.length = len;
      // 20. Return A.
      return A;
    };
  }());
}



// WEBPACK FOOTER //
// ./src/actions/save-to-pdf.js